/**
 * Dates: the API speaks ISO `YYYY-MM-DD`; the UI always shows `MM-DD-YYYY` (firm standard).
 * "Today" is the firm's business date in America/New_York, regardless of the viewer's zone.
 */
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

export const FIRM_TIMEZONE = 'America/New_York';
export const DISPLAY_DATE = 'MM-DD-YYYY';
export const ISO_DATE = 'YYYY-MM-DD';

/** `2026-10-05` → `10-05-2026`. Empty/invalid input → `''`. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = dayjs(iso.slice(0, 10), ISO_DATE, true);
  return d.isValid() ? d.format(DISPLAY_DATE) : '';
}

/** Timestamp → `10-05-2026 02:30 PM` in the firm's timezone. */
export function formatDateTime(isoInstant: string | null | undefined): string {
  if (!isoInstant) return '';
  const d = dayjs(isoInstant);
  return d.isValid() ? d.tz(FIRM_TIMEZONE).format(`${DISPLAY_DATE} hh:mm A`) : '';
}

/** `10-05-2026` → `2026-10-05`, or `null` when it is not a real date. */
export function parseDisplayDate(display: string): string | null {
  const d = dayjs(display.trim(), DISPLAY_DATE, true);
  return d.isValid() ? d.format(ISO_DATE) : null;
}

/** Whether `digit` at `pos` of a part-typed `MM-DD-YYYY` can still make a real date: no month
 *  past 12, no day past the month's last (February's 29th needs a leap year, once it's known),
 *  no year that can only end up before `minYear`. */
export function digitFitsDate(typed: string, pos: number, digit: string, minYear?: number): boolean {
  const next = typed.slice(0, pos) + digit + typed.slice(pos + 1);
  const month = next.slice(0, 2);
  const day = next.slice(3, 5);
  const year = next.slice(6, 10);
  const yearSoFar = /^\d*/.exec(year)?.[0] ?? '';
  if (minYear && yearSoFar && Number(yearSoFar.padEnd(4, '9')) < minYear) return false;
  if (Number(month[0]) > 1 || (/^\d\d$/.test(month) && (Number(month) < 1 || Number(month) > 12)))
    return false;
  // A year not finished yet doesn't rule out the 29th; 2024 is a leap year.
  const anyYear = /^\d{4}$/.test(year) ? year : '2024';
  const lastDay = /^\d\d$/.test(month) ? dayjs(`${anyYear}-${month}-01`).daysInMonth() : 31;
  if (Number(day[0]) * 10 > lastDay) return false;
  return !/^\d\d$/.test(day) || (Number(day) >= 1 && Number(day) <= lastDay);
}

/** The firm's business date as ISO, e.g. `2026-10-05`. */
export function firmTodayISO(now: Date = new Date()): string {
  return dayjs(now).tz(FIRM_TIMEZONE).format(ISO_DATE);
}
